import { useState, useCallback, useRef, useEffect } from 'react';

const PLAYBACK_SPEEDS = [0.1, 0.25, 0.5, 1, 2, 4, 8, 16];
const RENDER_INTERVAL_MS = 50;
const STEP_SIZE = 1 / 30;

/**
 * usePlayback — a time-range-based playback controller.
 *
 * This hook is the **single source of truth** for "what time are we viewing?"
 * All panels read `currentTimeSec` from this hook and independently resolve
 * their own data at that time.
 *
 * @param {number} durationSec — total playable duration in seconds (0 = disabled)
 */
export const usePlayback = (durationSec = 0) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [speed, setSpeed] = useState(1);
  const rafRef = useRef(null);
  const lastTickRef = useRef(null);
  const currentTimeRef = useRef(0);
  const lastFlushRef = useRef(0);

  const setTime = useCallback((val) => {
    currentTimeRef.current = val;
    setCurrentTimeSec(val);
  }, []);

  // ---- animation loop ----
  useEffect(() => {
    if (!isPlaying || durationSec <= 0) return;

    lastFlushRef.current = 0;

    const tick = (now) => {
      if (lastTickRef.current == null) {
        lastTickRef.current = now;
        lastFlushRef.current = now;
        rafRef.current = requestAnimationFrame(tick);
        return;
      }

      const wallDeltaMs = now - lastTickRef.current;
      lastTickRef.current = now;

      const advanceSec = (wallDeltaMs / 1000) * speed;
      const next = currentTimeRef.current + advanceSec;

      if (next >= durationSec) {
        setTime(durationSec);
        setIsPlaying(false);
        return;
      }

      currentTimeRef.current = next;

      if (now - lastFlushRef.current >= RENDER_INTERVAL_MS) {
        lastFlushRef.current = now;
        setCurrentTimeSec(next);
      }

      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      lastTickRef.current = null;
    };
  }, [isPlaying, durationSec, speed, setTime]);

  // ---- controls ----
  const play = useCallback(() => {
    setCurrentTimeSec((prev) => {
      const next = prev >= durationSec ? 0 : prev;
      currentTimeRef.current = next;
      return next;
    });
    setIsPlaying(true);
  }, [durationSec]);

  const pause = useCallback(() => setIsPlaying(false), []);

  const togglePlay = useCallback(() => {
    if (isPlaying) pause();
    else play();
  }, [isPlaying, play, pause]);

  const seekToTime = useCallback(
    (seconds) => setTime(Math.max(0, Math.min(seconds, durationSec))),
    [durationSec, setTime],
  );

  const stepForward = useCallback(() => {
    setCurrentTimeSec((prev) => {
      const next = Math.min(prev + STEP_SIZE, durationSec);
      currentTimeRef.current = next;
      return next;
    });
  }, [durationSec]);

  const stepBackward = useCallback(() => {
    setCurrentTimeSec((prev) => {
      const next = Math.max(prev - STEP_SIZE, 0);
      currentTimeRef.current = next;
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setIsPlaying(false);
    setTime(0);
    setSpeed(1);
  }, [setTime]);

  return {
    // state
    isPlaying,
    currentTimeSec,     // THE single source of truth — elapsed time in seconds
    speed,
    durationSec,        // echoed back for convenience
    // controls
    togglePlay,
    seekToTime,
    stepForward,
    stepBackward,
    setSpeed,
    reset,
    // constants
    PLAYBACK_SPEEDS,
  };
};
